/**
 * @page FlexEinsaetzePage
 * @route /flex/einsaetze
 * @description Flex-Worker Einsätze: KPI Cards, Liste, Neuer-Einsatz-Modal.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { CalendarClock, Plus, X, Users, CheckCircle, AlertCircle, Briefcase, Trash2 } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_MAP = {
  draft:        { label: 'flex.einsaetze.status.draft',      color: '#9CA3AF', bg: '#F3F4F6' },
  broadcast:    { label: 'flex.einsaetze.status.broadcast',   color: '#F59E0B', bg: '#FEF3C7' },
  voll:         { label: 'flex.einsaetze.status.voll',        color: '#10B981', bg: '#D1FAE5' },
  abgeschlossen:{ label: 'flex.einsaetze.status.abgeschlossen', color: '#338edc', bg: '#EBF4FD' },
};

function StatusBadge({ status, t }) {
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: s.bg, color: s.color }}>
      {t(s.label) || status}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' });
}

function formatTime(t) {
  if (!t) return '';
  return String(t).substring(0, 5);
}

export default function FlexEinsaetzePage() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [einsaetze, setEinsaetze] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const LIMIT = 50;

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/flex/einsaetze/stats`, { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch {}
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${API}/api/flex/einsaetze?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEinsaetze(data.einsaetze || []);
        setTotal(data.total || 0);
      }
    } catch {} finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const handleDeleteEinsatz = (e, einsatz) => {
    e.stopPropagation();
    setConfirmAction({
      title: t('flex.einsaetze.deleteTitle', 'Einsatz loeschen'),
      message: t('flex.einsaetze.confirmDelete') || `"${einsatz.titel}" wirklich loeschen?`,
      action: async () => {
        setDeleting(einsatz.id);
        try {
          const res = await fetch(`${API}/api/flex/einsaetze/${einsatz.id}`, { method: 'DELETE', credentials: 'include' });
          if (res.ok) { fetchList(); fetchStats(); }
          else { const d = await res.json().catch(() => ({})); setDeleteError(d.error || 'Fehler'); }
        } catch { setDeleteError('Netzwerkfehler'); }
        finally { setDeleting(null); }
      },
    });
  };

  const kpis = [
    { label: t('flex.einsaetze.stats.active'), value: stats?.aktive ?? '–', Icon: CalendarClock, iconColor: 'text-[#338edc]' },
    { label: t('flex.einsaetze.stats.openSlots'), value: stats?.offene_plaetze ?? '–', Icon: AlertCircle, iconColor: 'text-[#F59E0B]' },
    { label: t('flex.einsaetze.stats.confirmations'), value: stats?.zusagen_7t ?? '–', Icon: CheckCircle, iconColor: 'text-[#10B981]' },
    { label: t('flex.einsaetze.stats.poolSize'), value: stats?.pool_groesse ?? '–', Icon: Users, iconColor: 'text-[#6366F1]' },
  ];

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[var(--text-primary,#1A1A2E)]">{t('flex.einsaetze.title')}</h1>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors"
        >
          <Plus size={16} />
          {t('flex.einsaetze.new')}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <div key={i} className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5">
            <div className="flex items-center gap-2 mb-3">
              <kpi.Icon size={16} className={kpi.iconColor || "text-[var(--text-secondary,#4B5563)]"} />
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary,#4B5563)]">{kpi.label}</span>
            </div>
            <div className="text-2xl font-bold text-[var(--text-primary,#1A1A2E)]">{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)]"
        >
          <option value="">{t('flex.einsaetze.allStatuses')}</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => (
            <option key={k} value={k}>{t(v.label)}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border-table,#F3F4F6)]">
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">{t('flex.einsaetze.col.date')}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">{t('flex.einsaetze.col.title')}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">{t('flex.einsaetze.col.time')}</th>
              <th className="text-center px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">{t('flex.einsaetze.col.needed')}</th>
              <th className="text-center px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">{t('flex.einsaetze.col.confirmed')}</th>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)]">Status</th>
              <th className="px-4 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-[var(--text-tertiary,#6B7280)]">...</td></tr>
            ) : einsaetze.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-[var(--text-tertiary,#6B7280)]">{t('flex.einsaetze.empty')}</td></tr>
            ) : einsaetze.map(e => (
              <tr
                key={e.id}
                onClick={() => navigate(`/flex/einsaetze/${e.id}`)}
                className="border-b border-[var(--border-table,#F3F4F6)] hover:bg-[var(--bg-card-hover,#FAFAFE)] cursor-pointer transition-colors"
              >
                <td className="px-4 py-3.5 text-sm text-[var(--text-primary,#1A1A2E)]">
                  {formatDate(e.datum_von)}{e.datum_bis ? ` – ${formatDate(e.datum_bis)}` : ''}
                </td>
                <td className="px-4 py-3.5 text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{e.titel}</td>
                <td className="px-4 py-3.5 text-sm text-[var(--text-secondary,#4B5563)]">
                  {formatTime(e.uhrzeit_von)} – {formatTime(e.uhrzeit_bis)}
                </td>
                <td className="px-4 py-3.5 text-sm text-center text-[var(--text-primary,#1A1A2E)]">{e.benoetigt}</td>
                <td className="px-4 py-3.5 text-sm text-center text-[var(--text-primary,#1A1A2E)]">
                  {e.zugesagt}/{e.benoetigt}
                </td>
                <td className="px-4 py-3.5"><StatusBadge status={e.status} t={t} /></td>
                <td className="px-4 py-3.5">
                  {['draft', 'broadcast'].includes(e.status) && (
                    <button
                      onClick={ev => handleDeleteEinsatz(ev, e)}
                      disabled={deleting === e.id}
                      className="p-1.5 rounded-lg text-[var(--text-tertiary,#6B7280)] hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                      title={t('common.delete') || 'Löschen'}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showModal && (
        <CreateEinsatzModal
          t={t}
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); fetchList(); fetchStats(); }}
        />
      )}

      {deleteError && (
        <div className="fixed bottom-4 right-4 z-50 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl shadow-sm flex items-center gap-2">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}
      <ConfirmDialog
        open={!!confirmAction}
        onConfirm={() => { confirmAction?.action?.(); setConfirmAction(null); }}
        onCancel={() => setConfirmAction(null)}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmLabel={t('common.delete', 'Loeschen')}
        cancelLabel={t('common.cancel', 'Abbrechen')}
        variant="danger"
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  CREATE MODAL
// ═══════════════════════════════════════════════════════════════════

function CreateEinsatzModal({ t, onClose, onCreated }) {
  const [form, setForm] = useState({
    titel: '', beschreibung: '', datum_von: '', datum_bis: '',
    uhrzeit_von: '08:00', uhrzeit_bis: '16:00',
    aufgabe: '', benoetigt: 3, zusatz_info: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.titel || !form.datum_von || !form.uhrzeit_von || !form.uhrzeit_bis) {
      setError(t('flex.einsaetze.requiredFields'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = { ...form, benoetigt: parseInt(form.benoetigt, 10) || 1 };
      const res = await fetch(`${API}/api/flex/einsaetze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) onCreated();
      else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Fehler');
      }
    } catch { setError('Netzwerkfehler'); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:border-[#338edc] focus:ring-2 focus:ring-[rgba(51,142,220,0.1)] outline-none";
  const labelCls = "block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl shadow-sm w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">{t('flex.einsaetze.new')}</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-[var(--bg-card-hover,#F9FAFB)]"><X size={18} className="text-[var(--text-tertiary,#6B7280)]" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className={labelCls}>{t('flex.einsaetze.field.titel')} *</label>
            <input className={inputCls} value={form.titel} onChange={e => set('titel', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t('flex.einsaetze.field.beschreibung')}</label>
            <textarea className={inputCls + ' min-h-[60px]'} rows={2} value={form.beschreibung} onChange={e => set('beschreibung', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t('flex.einsaetze.field.datumVon')} *</label>
              <input type="date" className={inputCls} value={form.datum_von} onChange={e => set('datum_von', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>{t('flex.einsaetze.field.datumBis')}</label>
              <input type="date" className={inputCls} value={form.datum_bis} onChange={e => set('datum_bis', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t('flex.einsaetze.field.uhrzeitVon')} *</label>
              <input type="time" className={inputCls} value={form.uhrzeit_von} onChange={e => set('uhrzeit_von', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>{t('flex.einsaetze.field.uhrzeitBis')} *</label>
              <input type="time" className={inputCls} value={form.uhrzeit_bis} onChange={e => set('uhrzeit_bis', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>{t('flex.einsaetze.field.aufgabe')}</label>
            <input className={inputCls} value={form.aufgabe} onChange={e => set('aufgabe', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t('flex.einsaetze.field.benoetigt')} *</label>
            <input type="number" min="1" className={inputCls} value={form.benoetigt} onChange={e => set('benoetigt', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t('flex.einsaetze.field.zusatzInfo')}</label>
            <textarea className={inputCls + ' min-h-[50px]'} rows={2} value={form.zusatz_info} onChange={e => set('zusatz_info', e.target.value)} />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 pt-4 border-t border-[var(--border-card,#E5E7EB)]">
          <button onClick={onClose} className="px-4 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] text-sm font-medium text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-card-hover,#F9FAFB)]">
            {t('common.cancel') || 'Abbrechen'}
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-5 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
          >
            {saving ? '...' : t('flex.einsaetze.create')}
          </button>
        </div>
      </div>
    </div>
  );
}
